'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  X,
  ArrowRight,
  Clock,
} from 'lucide-react';
import { cn } from '@/utils';

interface SearchResult {
  id: string;
  title: string;
  description?: string;
  category?: string;
  href?: string;
}

interface SJCommandMenuProps {
  open: boolean;
  onClose: () => void;
  onSearch?: (query: string) => void;
  onSelect?: (result: SearchResult) => void;
  results?: SearchResult[];
  recentSearches?: string[];
  isLoading?: boolean;
  className?: string;
}

export function SJCommandMenu({
  open,
  onClose,
  onSearch,
  onSelect,
  results = [],
  recentSearches = [],
  isLoading = false,
  className,
}: SJCommandMenuProps) {
  const [query, setQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Display items - either results or recent searches
  const displayItems: SearchResult[] = query ? results : recentSearches.map((search) => ({
    id: search,
    title: search,
  }));

  // Auto-focus input when menu opens
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  // Reset state when menu closes
  useEffect(() => {
    if (!open) {
      setQuery('');
      setHighlightedIndex(0);
    }
  }, [open]);

  // Handle search input
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newQuery = e.target.value;
      setQuery(newQuery);
      setHighlightedIndex(0);
      onSearch?.(newQuery);
    },
    [onSearch]
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev < displayItems.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case 'Enter':
          e.preventDefault();
          if (displayItems[highlightedIndex]) {
            const item = displayItems[highlightedIndex];
            const result: SearchResult = {
              id: item.id,
              title: item.title,
              description: item.description,
              category: item.category,
              href: item.href,
            };
            onSelect?.(result);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    },
    [displayItems, highlightedIndex, onSelect, onClose]
  );

  // Handle result item click
  const handleResultClick = useCallback(
    (item: SearchResult) => {
      onSelect?.(item);
      onClose();
    },
    [onSelect, onClose]
  );

  // Handle overlay click
  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  // Render content only if we can access document.body
  if (typeof document === 'undefined') {
    return null;
  }

  const content = (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center"
          onClick={handleOverlayClick}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          <motion.div
            className={cn(
              'w-full max-w-xl mx-4 bg-[var(--sj-surface)] rounded-[20px] border border-[var(--sj-border)] shadow-xl overflow-hidden max-h-[70vh] flex flex-col',
              className
            )}
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Search Input Section */}
            <div className="px-4 py-3 flex items-center gap-3 bg-[var(--sj-surface)]">
              <Search className="h-5 w-5 text-[var(--sj-text-muted)] flex-shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="Search..."
                className="bg-transparent text-lg flex-1 outline-none border-0 text-[var(--sj-text-primary)] placeholder-[var(--sj-text-muted)]"
              />
              {query && (
                <button
                  onClick={() => {
                    setQuery('');
                    setHighlightedIndex(0);
                    onSearch?.('');
                  }}
                  className="p-1 hover:bg-[var(--sj-surface-muted)] rounded-[6px] transition-colors"
                >
                  <X className="h-4 w-4 text-[var(--sj-text-muted)]" />
                </button>
              )}
              <button
                onClick={onClose}
                className="p-1 hover:bg-[var(--sj-surface-muted)] rounded-[6px] transition-colors"
              >
                <X className="h-4 w-4 text-[var(--sj-text-muted)]" />
              </button>
            </div>

            {/* Divider */}
            <div className="h-px bg-[var(--sj-border)]" />

            {/* Results Area */}
            <div className="overflow-y-auto flex-1">
              {isLoading ? (
                // Skeleton Loading State
                <div className="px-4 py-3 space-y-2">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="h-10 rounded-[8px] bg-[var(--sj-surface-muted)] animate-pulse"
                    />
                  ))}
                </div>
              ) : query ? (
                // Results for search query
                results.length > 0 ? (
                  <div className="py-2">
                    {results.map((result, index) => (
                      <button
                        key={result.id}
                        onClick={() => handleResultClick(result)}
                        className={cn(
                          'w-full px-4 py-2.5 flex items-center gap-3 rounded-[8px] mx-2 cursor-pointer transition-colors group',
                          'hover:bg-[var(--sj-surface-muted)]',
                          highlightedIndex === index && 'bg-[var(--sj-surface-muted)]'
                        )}
                      >
                        <div className="flex-1 text-left">
                          <div className="flex items-center gap-2">
                            {result.category && (
                              <span className="text-[0.625rem] uppercase tracking-wider text-[var(--sj-text-muted)]">
                                {result.category}
                              </span>
                            )}
                            <span className="text-sm font-medium text-[var(--sj-text-primary)]">
                              {result.title}
                            </span>
                          </div>
                          {result.description && (
                            <p className="text-xs text-[var(--sj-text-muted)] truncate mt-0.5">
                              {result.description}
                            </p>
                          )}
                        </div>
                        <ArrowRight className="h-3.5 w-3.5 text-[var(--sj-text-muted)] opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                      </button>
                    ))}
                  </div>
                ) : (
                  // Empty state
                  <div className="px-4 py-8 text-center">
                    <p className="text-sm text-[var(--sj-text-muted)]">
                      No results found
                    </p>
                  </div>
                )
              ) : recentSearches.length > 0 ? (
                // Recent searches
                <div className="py-2">
                  <div className="px-4 py-2 text-xs uppercase tracking-wider text-[var(--sj-text-muted)]">
                    Recent
                  </div>
                  {recentSearches.map((search, index) => (
                    <button
                      key={search}
                      onClick={() => {
                        const result: SearchResult = {
                          id: search,
                          title: search,
                        };
                        handleResultClick(result);
                      }}
                      className={cn(
                        'w-full px-4 py-2.5 flex items-center gap-3 rounded-[8px] mx-2 cursor-pointer transition-colors group',
                        'hover:bg-[var(--sj-surface-muted)]',
                        highlightedIndex === index && 'bg-[var(--sj-surface-muted)]'
                      )}
                    >
                      <Clock className="h-3.5 w-3.5 text-[var(--sj-text-muted)] flex-shrink-0" />
                      <span className="text-sm text-[var(--sj-text-primary)] flex-1 text-left">
                        {search}
                      </span>
                      <ArrowRight className="h-3.5 w-3.5 text-[var(--sj-text-muted)] opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                    </button>
                  ))}
                </div>
              ) : (
                // Empty state when no query and no recent searches
                <div className="px-4 py-8 text-center">
                  <p className="text-sm text-[var(--sj-text-muted)]">
                    Start typing to search
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(content, document.body);
}
